import mergeWith from 'lodash/mergeWith';

import {
    updateForm,
    updateSelectOptionsByType,
    setAllFormFieldsAsModified,
} from '../../utils';
import * as types from './types';

const INITIAL_STATE = {
    bookingSessionId: undefined,
    bookingStatus: {},
    departLegIds: undefined,
    details: {},
    form: undefined,
    googlePlaceIds: {},
    insuranceOrderId: undefined,
    insuranceSelected: undefined,
    insuranceStatus: undefined,
    itineraryId: undefined,
    localities: {},
    orderIds: {},
    payment: {},
    paymentMethods: [],
    quoteSummary: undefined,
    returnLegIds: undefined,
    seats: undefined,
    tokenMap: undefined,
    tripConfirmations: {},
    tripIds: undefined,
    tripNotAvailable: false,
    userAccountExists: undefined,
    userEmail: undefined,
    validations: undefined,
    wheelchairs: undefined,
};

function setSeatInfo(state, action) {
    const { payload: data } = action;

    return {
        ...state,
        ...data,
    };
}

function setGoogleDepartId(state, action) {
    const { payload: depart } = action;

    return {
        ...state,
        googlePlaceIds: {
            ...state.googlePlaceIds,
            depart,
        },
    };
}

function setGoogleDestinationId(state, action) {
    const { payload: destination } = action;

    return {
        ...state,
        googlePlaceIds: {
            ...state.googlePlaceIds,
            destination,
        },
    };
}

function resetGooglePlaceIds(state) {
    return {
        ...state,
        googlePlaceIds: {},
    };
}

function setBookingSessionId(state, action) {
    const { payload: bookingSessionId } = action;

    return {
        ...state,
        bookingSessionId,
    };
}

function setBookingForm(state, action) {
    const { payload: form } = action;

    return {
        ...state,
        form,
    };
}

function setDetails(state, action) {
    const { payload: details } = action;

    return {
        ...state,
        details,
    };
}

function setPayment(state, action) {
    const { payload: payment } = action;

    return {
        ...state,
        payment,
    };
}

function setPaymentMethods(state, action) {
    const { payload: paymentMethods } = action;

    return {
        ...state,
        paymentMethods,
    };
}

function setValidations(state, action) {
    const { payload: validations } = action;

    return {
        ...state,
        validations,
    };
}

function setQuoteSummary(state, action) {
    const { payload: quoteSummary } = action;

    return {
        ...state,
        quoteSummary,
    };
}

function setBookingInfo(state, action) {
    const { payload: data } = action;

    return {
        ...state,
        departLegIds: data.departLegIds,
        itineraryId: data.itineraryId,
        returnLegIds: data.returnLegIds,
        selectedFaresMap: data.selectedFaresMap,
        tokenMap: data.tokenMap,
        tripIds: data.tripIds,
    };
}

function setLocalities(state, action) {
    const { payload: localities } = action;

    return {
        ...state,
        localities,
    };
}

function resetBookingInfo(state) {
    return INITIAL_STATE;
}

function setFieldValue(state, action) {
    const {
        path,
        value,
    } = action.payload;

    return {
        ...state,
        form: updateForm(state.form, path, { value }),
    };
}

function resetFieldValue(state, action) {
    const { payload: path } = action;

    return {
        ...state,
        form: updateForm(state.form, path, {
            isModified: false,
            value: undefined,
        }),
    };
}

function setFieldOptionsByPath(state, action) {
    const { path, values } = action.payload;

    return {
        ...state,
        form: updateForm(state.form, path, {
            isModified: false,
            values,
        }),
    };
}

function setFieldValidation(state, action) {
    const {
        path,
        value,
    } = action.payload;

    return {
        ...state,
        form: updateForm(state.form, path, { isValid: value }),
    };
}

function setFieldOptionsByType(state, action) {
    const { path, options } = action.payload;

    const { fieldType, values } = options;

    return {
        ...state,
        form: updateSelectOptionsByType(state.form, path, { fieldType, values }),
    };
}

function setOrderId(state, action) {
    const {
        meta: tripId,
        payload: orderId,
    } = action;

    return {
        ...state,
        orderIds: {
            ...state.orderIds,
            [tripId]: orderId,
        },
    };
}

function setBookingStatus(state, action) {
    const {
        meta: orderId,
        payload: data,
    } = action;

    return {
        ...state,
        bookingStatus: {
            ...state.bookingStatus,
            [orderId]: data,
        },
    };
}

function resetBookingStatus(state) {
    return {
        ...state,
        bookingStatus: INITIAL_STATE.bookingStatus,
    };
}

function setInsuranceOrderId(state, action) {
    const {
        payload: insuranceOrderId,
    } = action;

    return {
        ...state,
        insuranceOrderId,
    };
}

function setInsuranceStatus(state, action) {
    const {
        payload: data,
    } = action;

    return {
        ...state,
        insuranceStatus: data,
    };
}

function resetInsuranceStatus(state) {
    return {
        ...state,
        insuranceStatus: INITIAL_STATE.insuranceStatus,
    };
}

function setTripConfrimation(state, action) {
    const {
        meta: tripId,
        payload: data,
    } = action;

    return {
        ...state,
        tripConfirmations: {
            ...state.tripConfirmations,
            [tripId]: data,
        },
    };
}

function resetTripConfirmations(state) {
    return {
        ...state,
        tripConfirmations: INITIAL_STATE.tripConfirmations,
    };
}

function setInsuranceSelected(state, action) {
    const { payload } = action;

    return {
        ...state,
        insuranceSelected: payload,
    };
}

function resetInsuranceSelected(state) {
    return {
        ...state,
        insuranceSelected: INITIAL_STATE.insuranceSelected,
    };
}

function setAccountExists(state, action) {
    const { userAccountExists, userEmail } = action.payload;

    return {
        ...state,
        userAccountExists,
        userEmail,
    };
}

function setTripNotAvailable(state) {
    return {
        ...state,
        tripNotAvailable: true,
    };
}

function resetTripNotAvailable(state) {
    return {
        ...state,
        tripNotAvailable: false,
    };
}

function setAllFieldsAsModified(state) {
    return {
        ...state,
        form: setAllFormFieldsAsModified(state.form),
    };
}

function setFieldsAsModified(state, { payload }) {
    const { fields } = payload;

    const modifiedForm = setAllFormFieldsAsModified({ fields });
    const customizer = (objValue, srcValue, key) => {
        if (Array.isArray(objValue) && key === 'fields') {
            const mappedFields = srcValue.reduce((acc, field) => ({ ...acc, [field.id]: field }), {});

            return objValue.map((item) => {
                return mergeWith(item, mappedFields[item.id], customizer);
            });
        }

        return undefined;
    };

    const form = mergeWith({ ...state.form }, modifiedForm, customizer);

    return {
        ...state,
        form,
    };
}

const reducerFunctions = {
    [types.SET_SEAT_INFO]: setSeatInfo,
    [types.SET_BOOKING_SESSION_ID]: setBookingSessionId,
    [types.SET_BOOKING_FORM]: setBookingForm,
    [types.SET_QUOTE_SUMMARY]: setQuoteSummary,
    [types.SET_BOOKING_INFO]: setBookingInfo,
    [types.RESET_BOOKING_INFO]: resetBookingInfo,
    [types.SET_FIELD_OPTIONS_BY_TYPE]: setFieldOptionsByType,
    [types.SET_FIELD_OPTIONS_BY_PATH]: setFieldOptionsByPath,
    [types.SET_FIELD_VALUE]: setFieldValue,
    [types.RESET_FIELD_VALUE]: resetFieldValue,
    [types.SET_DETAILS]: setDetails,
    [types.SET_PAYMENT]: setPayment,
    [types.SET_PAYMENT_METHODS]: setPaymentMethods,
    [types.SET_VALIDATIONS]: setValidations,
    [types.SET_LOCALITIES]: setLocalities,
    [types.SET_ORDER_ID]: setOrderId,
    [types.SET_BOOKING_STATUS]: setBookingStatus,
    [types.RESET_BOOKING_STATUS]: resetBookingStatus,
    [types.SET_INSURANCE_SELECTED]: setInsuranceSelected,
    [types.RESET_INSURANCE_SELECTED]: resetInsuranceSelected,
    [types.SET_INSURANCE_ORDER_ID]: setInsuranceOrderId,
    [types.SET_INSURANCE_STATUS]: setInsuranceStatus,
    [types.RESET_INSURANCE_STATUS]: resetInsuranceStatus,
    [types.SET_TRIP_CONFIRMATION]: setTripConfrimation,
    [types.RESET_TRIP_CONFIRMATIONS]: resetTripConfirmations,
    [types.SET_ACCOUNT_EXISTS]: setAccountExists,
    [types.SET_FIELD_VALIDATION]: setFieldValidation,
    [types.SET_TRIP_NOT_AVAILABLE]: setTripNotAvailable,
    [types.RESET_TRIP_NOT_AVAILABLE]: resetTripNotAvailable,
    [types.SET_GOOGLE_DEPART_ID]: setGoogleDepartId,
    [types.SET_GOOGLE_DESTINATION_ID]: setGoogleDestinationId,
    [types.RESET_GOOGLE_PLACE_IDS]: resetGooglePlaceIds,
    [types.SET_ALL_FIELDS_AS_MODIFIED]: setAllFieldsAsModified,
    [types.SET_FIELDS_AS_MODIFIED]: setFieldsAsModified,
};

export {
    INITIAL_STATE,
};

export default function reducer(state = INITIAL_STATE, action = {}) {
    if (!Object.prototype.hasOwnProperty.call(reducerFunctions, action.type)) {
        return state;
    }

    return reducerFunctions[action.type](state, action);
}
